import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SettingsCtx = createContext({
  settings: null,
  loading: true,
  refresh: () => {},
});

// Rich fallback — matches backend defaults so if /api/settings ever fails
// the UI still renders correctly and any "Save" round-trip stays sane.
const FALLBACK = {
  brand: { name: "Koinonia Church", tagline: "Operations · Tasks · Finance", letter: "K" },
  currency: { code: "INR", symbol: "₹", name: "Indian Rupee" },
  expense_categories: [
    "Tithes & Offerings",
    "Pastoral & Staff Salaries",
    "Worship & Music",
    "Sound & AV Equipment",
    "Children's Ministry",
    "Youth Ministry",
    "Outreach & Missions",
    "Charity & Welfare",
    "Sunday Service Expenses",
    "Hospitality & Refreshments",
    "Discipleship & Training",
    "Conferences & Retreats",
    "Travel & Transportation",
    "Office & Administration",
    "Printing & Literature",
    "Building & Maintenance",
    "Utilities",
    "Bank Charges & Fees",
    "Equipment & Supplies",
    "Other",
  ],
  currency_presets: [
    { code: "INR", symbol: "₹", name: "Indian Rupee" },
    { code: "USD", symbol: "$", name: "US Dollar" },
    { code: "EUR", symbol: "€", name: "Euro" },
    { code: "GBP", symbol: "£", name: "British Pound" },
    { code: "AUD", symbol: "A$", name: "Australian Dollar" },
    { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
    { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
    { code: "AED", symbol: "AED", name: "UAE Dirham" },
    { code: "ZAR", symbol: "R", name: "South African Rand" },
    { code: "NGN", symbol: "₦", name: "Nigerian Naira" },
    { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  ],
};

export const SettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/settings");
      setSettings({ ...FALLBACK, ...r.data });
    } catch (e) {
      // keep fallback
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch whenever the auth user changes (covers the race where /api/settings
  // is called before the auth cookie / token is in place).
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh, user?.user_id]);

  // Keep document title in sync with brand
  useEffect(() => {
    const name = settings?.brand?.name;
    if (name) document.title = name;
  }, [settings]);

  return (
    <SettingsCtx.Provider value={{ settings, loading, refresh, setSettings }}>
      {children}
    </SettingsCtx.Provider>
  );
};

export const useSettings = () => useContext(SettingsCtx);
